import toast from "react-hot-toast";
import { withTranslation } from "react-i18next";

import { CircleCheckIcon, DownloadIcon } from "@lucide/icons";

import { styled } from "simplydo/react/styled";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";

import type { IdeaCompanyVerificationProps } from "./types";

const VerificationContainer = styled.div`
  border-radius: 5px;
  box-shadow:
    0 0 0 1px #d4d4d5,
    0 2px #16ab39,
    0 1px 3px #bcbdbd;
  padding: 15px;
  > .header {
    font-weight: bold;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
`;

function IdeaCompanyVerification({
  idea,
  report,
  updateReportUrl,
  field,
}: IdeaCompanyVerificationProps) {
  if (!report || !idea) return null;
  return (
    <VerificationContainer>
      <div className="header">
        <Icon icon={CircleCheckIcon} color="green" /> Company successfully found
      </div>
      <Table>
        <TableBody>
          <TableRow>
            <TableHeaderCell collapsing>Name</TableHeaderCell>
            <TableCell>{report.companyName}</TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell collapsing>Registration</TableHeaderCell>
            <TableCell>{report.companyNumber}</TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell collapsing>Address</TableHeaderCell>
            <TableCell>{report.companyAddress}</TableCell>
          </TableRow>
          {report.companyVatNumber && (
            <TableRow>
              <TableHeaderCell collapsing>VAT number</TableHeaderCell>
              <TableCell>{report.companyVatNumber}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {report.reportVisible ? (
        report.reportUrl ? (
          <ButtonLink
            fluid
            color="primary"
            to={report.reportUrl || report.tempReportUrl || ""}
            target="_blank"
            rel="noopener noreferrer"
            download="report.pdf"
          >
            <Icon icon={DownloadIcon} />
            Download report
          </ButtonLink>
        ) : (
          <Button
            onClick={() => {
              api.verifications.createReport(
                {
                  id: report.companyId || report.report || report._id,
                  forType: "idea",
                  forId: idea._id,
                  templatedFieldId: field.id,
                },
                (data: import("./types").CreditsafeCreateReportResponse) => {
                  const reportUrl =
                    data?.report?.tempReportUrl ?? data?.report?.reportUrl;
                  if (reportUrl) {
                    toast.success("Report generated successfully");
                    updateReportUrl(reportUrl);
                    window.open(reportUrl, "_blank");
                  } else {
                    toast.error("Failed to generate report");
                  }
                },
                () => {
                  toast.error("Failed to generate report");
                },
              );
            }}
            fluid
            color="primary"
          >
            <Icon icon={DownloadIcon} />
            Download report
          </Button>
        )
      ) : null}
    </VerificationContainer>
  );
}

export default withTranslation()(IdeaCompanyVerification);
